import GeoJSON from 'ol/format/GeoJSON';
import type Point from 'ol/geom/Point';

import { ajax } from 'cadenza/api-client/api';
import type { CoordinateDto } from 'cadenza/api-client/dto/coordinate-dto';
import type { GeocodingConfigDto } from 'cadenza/geocoding/dto/geocoding-config-dto';
import type { GeocodingParametersDto } from 'cadenza/geocoding/dto/geocoding-parameters-dto';
import type { ReverseGeocodedLocationDto } from 'cadenza/geocoding/dto/reverse-geocoded-location-dto';
import type { ObjecttypeId } from 'cadenza/repository/objecttype-id';

/**
 * Find address at the specified location.
 *
 * @param point - location to be reverse-geocoded
 * @param srsName - coordinate system
 * @return address in structured and formatted form, with distance to specified location
 */
export function findAddress (point: Point, srsName: string) {
  const params = new URLSearchParams();
  const pointGeometry = new GeoJSON().writeGeometry(point);
  params.set('point', pointGeometry);
  params.set('srsName', srsName);
  return ajax<ReverseGeocodedLocationDto>(`/reverseGeocoding?${params.toString()}`);
}

/**
 * Find address at the specified coordinate.
 *
 * @param coordinate - x and y value of coordinate to be reverse-geocoded
 * @param srsName - coordinate system
 * @return address in structured and formatted form, with distance to specified location
 */
export function findAddressAtCoordinate (coordinate: CoordinateDto, srsName: string) {
  const point = JSON.stringify({
    type: 'Point',
    coordinates: [ coordinate[0].toString(), coordinate[1].toString() ]
  });
  const params = new URLSearchParams({ point, srsName });
  return ajax<ReverseGeocodedLocationDto>(`/reverseGeocoding?${params.toString()}`);
}

/**
 * Returns configuration for geocoding operation.
 * This contains information what types are available (structured address and
 * unstructured address), limit and default name mapping for
 * preselected attributes/columns.
 *
 * @return a promise containing a json object with config properties
 * @see net/disy/cadenza/workbook/web/operation/geocoding/rest/OperationConfigEndpointController.java
 */
export function getGeocodingConfiguration () {
  return ajax<GeocodingConfigDto>('/operation/config/geocoding', {
    type: 'GET'
  });
}

/**
 * Returns previously used geocoding parameters for the given objecttype ID or null if there was
 * no request using this ID. These parameters are used for restoring geocoding form values.
 *
 * @param objecttypeId - The id of an objecttype that will have geocoding parameters
 * edited.
 * @return - Object referring to either StructuredGeocodingParametersDto or
 * UnstructuredGeocodingParametersDto or null (meaning there were no preselected geocoding values).
 * @see net/disy/cadenza/workbook/web/operation/geocoding/rest/OperationConfigEndpointController.java
 */
export function getGeocodingParameters (objecttypeId: ObjecttypeId) {
  return ajax<GeocodingParametersDto>(`/operation/parameters/${btoa(objecttypeId)}`, {
    type: 'GET'
  });
}
